// Lesson packs (EDU-1, ARCH §29.2). PURE (ENG-1).
//
// The structural shape of a lesson is zod's (`registry/schemas.ts` `lessonSchema`);
// the rules that span fields — or reach into the rest of the registry — are here,
// and run at registry load so a bad pack is a load error (`pnpm audit:registry`
// red), never a lesson that breaks mid-play. Same split as heredity's gene tables
// (`heredity/genes.ts` `validateGenes`).
import { EDU } from '../config';
import { KNOWN_GAMERULES } from '../game/commands/core';
import { parseWorldFile } from '../save/worldfile';
import type { CriterionData, LessonDef } from '../registry/schemas';

export type { LessonDef, ObjectiveDef, CriterionData, QuizDef, QuestionData, RegionDef, EduTriggerKind } from '../registry/schemas';

/** The slice of the merged registry a lesson's criteria may reference. Values are
 *  never read — only `.has()` — so any registry's name-keyed maps satisfy this,
 *  and `education/lessons.ts` never needs `registry/load.ts`'s `Registries` type
 *  (which itself will hold `lessons`, so importing it here would be circular). */
export interface LessonRegRefs {
  readonly mobs: ReadonlyMap<string, unknown>;
  readonly blocks: ReadonlyMap<string, unknown>;
  readonly items: ReadonlyMap<string, unknown>;
}

const utf8 = new TextEncoder();

function checkCriterionRefs(c: CriterionData, refs: LessonRegRefs, regionNames: ReadonlySet<string>): string | null {
  switch (c.trigger) {
    case 'notebook_record':
    case 'mob_bred':
      return refs.mobs.has(c.species) ? null : `criterion names unknown mob '${c.species}'`;
    case 'region_entered':
      return regionNames.has(c.region) ? null : `criterion names undeclared region '${c.region}'`;
    case 'crop_harvested':
      return refs.blocks.has(c.crop) ? null : `criterion names unknown block '${c.crop}'`;
    case 'instrument_read':
      return refs.items.has(c.instrument) ? null : `criterion names unknown item '${c.instrument}'`;
    case 'population_count':
      return refs.mobs.has(c.species) ? null : `criterion names unknown mob '${c.species}'`;
    case 'beacon_found':
    case 'aircraft_landed':
    case 'autopilot_hold':
    case 'capacity_message':
      return null;
  }
}

/** Check a lesson against §29.2's load-time rules. Returns the FIRST violation as a
 *  message naming the offending member, or null — the caller (`registry/load.ts`)
 *  prefixes it with the lesson's own name, matching `validateGenes`. */
export function validateLesson(lesson: LessonDef, refs: LessonRegRefs): string | null {
  if (lesson.objectives.length > EDU.MAX_OBJECTIVES) {
    return `at most ${EDU.MAX_OBJECTIVES} objectives (has ${lesson.objectives.length})`;
  }
  if (lesson.quiz && lesson.quiz.questions.length > EDU.MAX_QUESTIONS) {
    return `at most ${EDU.MAX_QUESTIONS} quiz questions (has ${lesson.quiz.questions.length})`;
  }
  if (lesson.regions && lesson.regions.length > EDU.MAX_REGIONS) {
    return `at most ${EDU.MAX_REGIONS} regions (has ${lesson.regions.length})`;
  }

  const objNames = new Set<string>();
  const afterOf = new Map<string, string | undefined>();
  for (const o of lesson.objectives) {
    if (objNames.has(o.name)) return `objective '${o.name}' is declared twice`;
    objNames.add(o.name);
    afterOf.set(o.name, o.after);
  }
  for (const o of lesson.objectives) {
    if (o.after !== undefined && !objNames.has(o.after)) return `objective '${o.name}' names unknown after '${o.after}'`;
  }
  if (lesson.quiz?.after !== undefined && !objNames.has(lesson.quiz.after)) {
    return `quiz names unknown after '${lesson.quiz.after}'`;
  }
  // Acyclic `after` (§29.2): walk each objective's chain; a repeat means a cycle.
  for (const start of objNames) {
    const seen = new Set<string>([start]);
    let cur = afterOf.get(start);
    while (cur !== undefined) {
      if (seen.has(cur)) return `objective 'after' chain is cyclic at '${start}'`;
      seen.add(cur);
      cur = afterOf.get(cur);
    }
  }

  const regionNames = new Set((lesson.regions ?? []).map((r) => r.name));
  for (const o of lesson.objectives) {
    for (const c of o.criteria) {
      const bad = checkCriterionRefs(c, refs, regionNames);
      if (bad) return `objective '${o.name}': ${bad}`;
    }
  }

  if (lesson.template !== undefined) {
    const bytes = utf8.encode(lesson.template).byteLength;
    if (bytes > EDU.TEMPLATE_MAX_BYTES) return `template is ${bytes} bytes, over the ${EDU.TEMPLATE_MAX_BYTES} cap`;
    try {
      parseWorldFile(lesson.template);
    } catch (e) {
      return `template does not parse: ${(e as Error).message}`;
    }
  }

  if (lesson.gamerules) {
    for (const key of Object.keys(lesson.gamerules)) {
      if (!KNOWN_GAMERULES.includes(key)) return `unknown gamerule '${key}'`;
    }
  }

  return null;
}

/** Look up a lesson by its `name` (the overlay/objective key, D-TF-188). */
export function lessonByName(lessons: ReadonlyMap<string, LessonDef>, name: string): LessonDef | undefined {
  return lessons.get(name);
}
